import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import type { FeedbackParams } from "@memorang/features/src/feedback/types";
import type { ReportDetails } from "@memorang/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";

type Params = {
	reportDetails: ReportDetails;
	tenantName: string;
	outputLanguage: string;
};

export const useReportFeedbackDialogParams = ({
	reportDetails,
	tenantName,
	outputLanguage,
}: Params): FeedbackParams => {
	const { t } = useTranslation();

	const companyName = useAppStore((s) => s.companyName);
	const appName = useAppStore((s) => s.app.name);
	const currentAppVersion = useAppStore((s) => s.app.version);

	const {
		currentExamContext: { projectId, organizationId },
	} = useExamContext();

	const { username, email } = useAuthContext();

	return useMemo((): FeedbackParams => {
		const examName = reportDetails.examName;
		const productName = t("report.productName", {
			examName,
			type:
				reportDetails.reportSessionType === "SUMMATIVE"
					? t("report.practiceTestCompleted")
					: t("report.studyPack"),
		});

		return {
			currentAppVersion,
			userName: username,
			email,
			projectId,
			organizationId,
			appLanguage: outputLanguage,
			product: productName,
			appName,
			platform: Platform.OS,
			view: "Session Report",
			tenantId: tenantName,
			context: examName,
			feature: "AI Session Analysis",
			sessionId: reportDetails.id,
			companyName,
		};
	}, [
		reportDetails,
		t,
		currentAppVersion,
		username,
		email,
		projectId,
		organizationId,
		outputLanguage,
		appName,
		tenantName,
		companyName,
	]);
};
